/**
 * Automation — where to automate first, and which products would do it.
 *
 * The top-ranked document type is opened in full; the rest expand in place to the
 * same anatomy, so there is one layout to learn rather than two. Each type shows
 * the pipeline it would run — **only the stages it actually needs** — with every
 * product mounted on the stages it would own.
 *
 * Two things are deliberately absent from the page:
 *
 * - **A classify stage.** Docsight is itself the classifier; a document reaches
 *   this pipeline with its type already established, which is the only reason a
 *   ranking exists at all. The page shows the steps that happen and does not
 *   explain the one that does not.
 * - **The score as a headline.** Measured on real data it spans 93–98 across the
 *   busiest fourteen types, because two of its three components are near-constant
 *   by construction. It is kept as evidence in the drawer, where it belongs, and
 *   volume does the ranking.
 *
 * Everything drawn here comes from one request. Nothing is derived and cached
 * client-side, so `Recompute` genuinely changes the page.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError } from '../../api/errors';
import type {
  ProductView,
  ShortlistRowView,
  StageView,
  StrongestFitView,
} from '../../api/schema';
import { useSession } from '../../auth/session';
import { Button, EmptyState, ErrorState, Skeleton, useToast } from '../../components';
import { OPEN_CHAT_EVENT } from '../chatbot';

import './shortlist.css';
import { fmtBig, fmtConfidence, fmtPct } from './format';
import { STAGE_MEANING, mountLabel, productHue } from './productMeta';
import { ProductMark } from './products';
import { revealOnEnter, revealPanel } from './reveal';
import { useShortlist, type ShortlistState } from './useShortlist';

const ASK_PROMPT = 'Where should we automate first?';

export function AutomationScreen(): React.JSX.Element {
  const state = useShortlist();
  return <AutomationView state={state} />;
}

function AutomationView({ state }: { state: ShortlistState }): React.JSX.Element {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { api, hasPermission } = useSession();
  const canRecompute = hasPermission('automation:recompute');
  const [recomputing, setRecomputing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const reload = state.status === 'loading' ? undefined : state.reload;

  const recompute = useCallback(() => {
    setRecomputing(true);
    void api.automation
      .recompute()
      .then((result) => {
        toast(`Rescored ${fmtBig(result.count)} clusters from your current documents`);
        // Re-fetch rather than patching anything locally: the ranking, the gates
        // and which types clear them are all server-side, so the only correct
        // client response to a recompute is to ask again.
        reload?.();
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 429) {
          toast("Today's recompute limit is used up for this tenant — try again tomorrow.", {
            tone: 'error',
          });
        } else {
          toast('Could not recompute — please retry.', { tone: 'error' });
        }
      })
      .finally(() => {
        setRecomputing(false);
      });
  }, [api, reload, toast]);

  const ask = useCallback(() => {
    window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT, { detail: { message: ASK_PROMPT } }));
  }, []);

  const explore = useCallback(
    (row: ShortlistRowView) => {
      void navigate(`/documents?type=${encodeURIComponent(row.category_name)}`);
      toast('Documents filtered to this type');
    },
    [navigate, toast],
  );

  const stage = useRef<HTMLElement>(null);
  const ready = state.status === 'ready';
  // Keyed on the row count so a recompute that changes the shortlist replays the
  // entrance for the new set rather than leaving fresh rows sitting hidden.
  const rowCount = ready ? state.data.rows.length : 0;
  useEffect(() => {
    if (!ready) return undefined;
    const root = stage.current;
    if (root === null) return undefined;
    const above = revealOnEnter(
      root.querySelectorAll('.ds-sl__top, .ds-sl__hero, .ds-sl__sechd'),
      { stagger: 70, immediate: true },
    );
    const below = revealOnEnter(root.querySelectorAll('.ds-sl__row, .ds-sl__notes'), {
      stagger: 55,
    });
    return () => {
      above();
      below();
    };
  }, [ready, rowCount]);

  if (state.status === 'loading') return <Loading />;
  if (state.status === 'error') {
    return (
      <section className="ds-sl" aria-label="Automation opportunities">
        <ErrorState
          title="We couldn't load your automation shortlist"
          body="The analytics service didn't respond. Your documents are safe — this only affects the ranking."
          traceId={state.traceId ?? undefined}
          actions={
            <Button onClick={state.reload} variant="primary">
              Retry
            </Button>
          }
        />
      </section>
    );
  }

  const { data } = state;
  const [lead, ...rest] = data.rows;

  return (
    <section ref={stage} className="ds-sl" aria-label="Automation opportunities">
      <div className="ds-sl__top">
        <div className="ds-sl__intro">
          <h2>Where to automate first — and what we&apos;d use to do it</h2>
          <p>{summarise(data)}</p>
        </div>
        {canRecompute ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={recompute}
            loading={recomputing}
            loadingLabel="Rescoring…"
          >
            Recompute now
          </Button>
        ) : null}
      </div>

      {data.catalogue_size === 0 ? (
        <NoCatalogue />
      ) : data.rows.length === 0 ? (
        <NothingYet data={data} />
      ) : (
        <>
          {lead !== undefined ? (
            <Hero row={lead} rank={1} onExplore={explore} onAsk={ask} />
          ) : null}

          {rest.length > 0 ? (
            <>
              <div className="ds-sl__sechd">
                <h3>The rest of the shortlist</h3>
                <p>Ranked by volume. Open one for its full anatomy.</p>
              </div>
              <ul className="ds-sl__rows">
                {rest.map((row, index) => (
                  <Row
                    key={row.id}
                    row={row}
                    rank={index + 2}
                    open={openId === row.id}
                    onToggle={() => {
                      setOpenId((current) => (current === row.id ? null : row.id));
                    }}
                    onExplore={explore}
                    onAsk={ask}
                  />
                ))}
              </ul>
            </>
          ) : null}

          <Footnotes data={data} />
        </>
      )}
    </section>
  );
}

/** The intro line, stated from the response rather than hardcoded. */
function summarise(data: {
  rows: ShortlistRowView[];
  qualified: number;
  considered: number;
  min_volume: number;
}): string {
  const shown = data.rows.length;
  const types = data.considered === 1 ? '1 document type' : `${String(data.considered)} document types`;
  return (
    `${String(shown)} of your ${types} clear ${fmtBig(data.min_volume)} documents a year ` +
    `and have a product that genuinely handles them. The strongest is opened in full; ` +
    `the rest expand in place.`
  );
}

// ------------------------------------------------------------------------ hero

function Hero({
  row,
  rank,
  onExplore,
  onAsk,
}: {
  row: ShortlistRowView;
  rank: number;
  onExplore: (row: ShortlistRowView) => void;
  onAsk: () => void;
}): React.JSX.Element {
  const panel = useRef<HTMLElement>(null);
  // Keyed on the row id so a recompute that changes the leading type replays the
  // sequence for the new one, rather than showing it already settled.
  useEffect(() => revealPanel(panel.current, { from: 120, step: 90 }), [row.id]);

  return (
    <section
      ref={panel}
      className="ds-sl__hero"
      aria-label={`Top opportunity: ${row.category_name}`}
    >
      <div className="ds-sl__herotop">
        <div className="ds-sl__heroid">
          <div className="ds-sl__rankline">
            <span className="ds-sl__rk">RANK {rank}</span>
            <span className="ds-sl__eyebrow">Top opportunity</span>
          </div>
          <h3>{row.category_name}</h3>
        </div>
        <div className="ds-sl__figs">
          <Figure value={fmtBig(row.signals.volume)} label="docs / yr" to={row.signals.volume} />
          <Figure value={fmtBig(row.signals.pages)} label="pages" to={row.signals.pages} />
          <Figure
            value={fmtConfidence(row.signals.avg_confidence)}
            label="confidence"
            to={row.signals.avg_confidence}
            kind="conf"
            tone="ok"
          />
          <Figure value={String(row.steps)} label="pipeline steps" to={row.steps} />
        </div>
      </div>
      <Anatomy row={row} onExplore={onExplore} onAsk={onAsk} />
    </section>
  );
}

/**
 * One hero figure. `data-to`/`data-kind` are what the choreography counts up to;
 * the rendered text is the final value, so the figure is correct and readable even
 * if the script never runs.
 */
function Figure({
  value,
  label,
  to,
  kind = 'int',
  tone,
}: {
  value: string;
  label: string;
  to: number;
  kind?: 'int' | 'conf' | 'pct';
  tone?: 'ok';
}): React.JSX.Element {
  return (
    <div className={`ds-sl__fig${tone === 'ok' ? ' is-ok' : ''}`}>
      <b className="mono" data-to={to} data-kind={kind}>
        {value}
      </b>
      <span>{label}</span>
    </div>
  );
}

// ------------------------------------------------------------------------- row

function Row({
  row,
  rank,
  open,
  onToggle,
  onExplore,
  onAsk,
}: {
  row: ShortlistRowView;
  rank: number;
  open: boolean;
  onToggle: () => void;
  onExplore: (row: ShortlistRowView) => void;
  onAsk: () => void;
}): React.JSX.Element {
  const products = [...row.pipeline_products, ...row.standalone_products];
  const body = useRef<HTMLDivElement>(null);
  // Faster than the hero: this is answering a click, and past roughly a second and
  // a half motion stops reading as motion and starts reading as lag.
  useEffect(() => {
    if (!open) return undefined;
    return revealPanel(body.current, { from: 60, step: 48 });
  }, [open, row.id]);

  return (
    <li className="ds-sl__row" data-open={open ? '1' : '0'}>
      <button
        type="button"
        className="ds-sl__rowbtn"
        aria-expanded={open}
        aria-controls={`ds-sl-body-${row.id}`}
        onClick={onToggle}
      >
        <span className="ds-sl__rowrk mono">#{rank}</span>
        <span className="ds-sl__rowname">
          {row.category_name}
          <small>
            {row.steps} steps · {fmtConfidence(row.signals.avg_confidence)} confidence
          </small>
        </span>
        {row.strongest_fit !== null ? (
          <span
            className="ds-sl__best"
            style={{ '--cc': productHue(row.strongest_fit.product_key) } as React.CSSProperties}
          >
            <i />
            <em className="mono">BEST</em>
            {row.strongest_fit.product_name}
          </span>
        ) : null}
        <span className="ds-sl__chips">
          {products
            .filter((product) => product.product_key !== row.strongest_fit?.product_key)
            .map((product) => (
              <span
                key={product.product_key}
                className="ds-sl__chip"
                style={{ '--cc': productHue(product.product_key) } as React.CSSProperties}
              >
                <i />
                {product.product_name} · {product.fit}
              </span>
            ))}
        </span>
        <span className="ds-sl__rowvol">
          <b className="mono">{fmtBig(row.signals.volume)}</b>
          <span>docs / yr</span>
        </span>
        <span className="ds-sl__caret" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 16 16">
            <path
              d="M6 3.5 L10.5 8 L6 12.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {/* Rendered only while open: the anatomy is a dozen nodes and cards, and
          mounting it for every collapsed row would build eleven of them nobody
          has asked to see. */}
      <div className="ds-sl__body" id={`ds-sl-body-${row.id}`}>
        <div ref={body}>{open ? <Anatomy row={row} onExplore={onExplore} onAsk={onAsk} /> : null}</div>
      </div>
    </li>
  );
}

/** The one product to start with, and why it beats the others.

    Not a re-sort of the cards below. The list answers *which products fit*; this
    answers *which one addresses what this document type is for* — a different
    question the model is asked separately. On the real corpus the two disagree:
    Letter has two products tied at 0.90 that no ordering can break, and Medical
    Record ranks IntelieDocs highest on coverage while the document exists so a
    patient can understand it. `diverges_from_top_confidence` is exactly that case,
    and it is said out loud rather than left to be inferred from the numbers. */
function StrongestFit({ lead }: { lead: StrongestFitView }): React.JSX.Element {
  return (
    <div
      className="ds-sl__lead is-in"
      style={{ '--cc': productHue(lead.product_key) } as React.CSSProperties}
    >
      <span className="ds-sl__leadmark" aria-hidden="true">
        <ProductMark productKey={lead.product_key} />
      </span>
      <div className="ds-sl__leadbody">
        <span className="ds-sl__eyebrow">Strongest fit · start here</span>
        <h4>
          {lead.product_name}
          {lead.diverges_from_top_confidence ? (
            <small className="mono">not the highest score — chosen on purpose</small>
          ) : null}
        </h4>
        {lead.why ? <p>{lead.why}</p> : null}
      </div>
      <span className="ds-sl__leadfit mono" aria-label={`Fit ${fmtConfidence(lead.confidence)}`}>
        {Math.round(lead.confidence * 100)}
      </span>
    </div>
  );
}

// --------------------------------------------------------------------- anatomy

/** The shared body: pipeline, product cards, standalone products, evidence. */
function Anatomy({
  row,
  onExplore,
  onAsk,
}: {
  row: ShortlistRowView;
  onExplore: (row: ShortlistRowView) => void;
  onAsk: () => void;
}): React.JSX.Element {
  return (
    <>
      {/* Above the spine on purpose. The winner is often a STANDALONE product,
          which renders in the separate-products block right at the bottom — so
          placing this below would put the answer last on the page. */}
      {row.strongest_fit !== null ? <StrongestFit lead={row.strongest_fit} /> : null}
      <Pipeline stages={row.pipeline} />
      {row.pipeline_products.length > 0 ? (
        <div className="ds-sl__cards">
          {row.pipeline_products.map((product) => (
            <ProductCard key={product.product_key} product={product} />
          ))}
        </div>
      ) : null}
      {row.standalone_products.length > 0 ? (
        <div className="ds-sl__sep is-in">
          <div className="ds-sl__sephd">
            <span className="ds-sl__eyebrow">
              Separate product{row.standalone_products.length > 1 ? 's' : ''}
            </span>
            <span>runs alongside this pipeline, not inside it — its own reading, its own output</span>
          </div>
          <div className="ds-sl__cards">
            {row.standalone_products.map((product) => (
              <ProductCard key={product.product_key} product={product} outside />
            ))}
          </div>
        </div>
      ) : null}
      <Evidence row={row} onExplore={onExplore} onAsk={onAsk} />
    </>
  );
}

function Pipeline({ stages }: { stages: StageView[] }): React.JSX.Element | null {
  if (stages.length === 0) return null;
  return (
    <div className="ds-sl__spinewrap">
      <div className="ds-sl__spinehd">
        <span className="ds-sl__eyebrow">{stages.length} steps</span>
        <span>the pipeline this type would run, and the product that owns each stage</span>
      </div>
      <div className="ds-sl__spinescroll">
        <div
          className="ds-sl__spine"
          style={{ '--n': stages.length } as React.CSSProperties}
          role="list"
          aria-label="Pipeline stages"
        >
          {/* A pulse running the rail, so the diagram reads as documents moving
              through rather than a static chain. Hidden under reduced motion. */}
          <span className="ds-sl__flow" aria-hidden="true">
            <i />
          </span>
          {stages.map((mounted, index) => (
            <div
              key={mounted.stage}
              role="listitem"
              className={`ds-sl__stg is-lit${index === 0 ? ' is-first' : ''}${
                index === stages.length - 1 ? ' is-last' : ''
              }`}
              /* The FIRST owner's hue colours the node, and any second owner is
                 listed beneath it — a stage with two owners is a fan-out (a report
                 is both exported and indexed), not a tie to be broken. `?? ''`
                 rather than a non-null assertion: the server never emits a stage
                 with no owners, but the type permits it and a silent fallback beats
                 a runtime crash if that ever changes. */
              style={
                { '--sc': productHue(mounted.owners[0]?.product_key ?? '') } as React.CSSProperties
              }
              title={`${mounted.stage} — ${STAGE_MEANING[mounted.stage]}`}
            >
              <i />
              <em>{mounted.stage}</em>
              {mounted.owners.map((owner) => (
                <span
                  key={owner.product_key}
                  className="ds-sl__mount mono"
                  style={{ color: productHue(owner.product_key) }}
                >
                  {mountLabel(owner.product_name)}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProductCard({
  product,
  outside = false,
}: {
  product: ProductView;
  outside?: boolean;
}): React.JSX.Element {
  const hue = productHue(product.product_key);
  return (
    <article className="ds-sl__card is-in" style={{ '--pc': hue } as React.CSSProperties}>
      <div className="ds-sl__cardhd">
        <ProductMark productKey={product.product_key} />
        <h4>
          {product.product_name}
          {outside ? <span className="ds-sl__outside">outside the pipeline</span> : null}
        </h4>
        <FitRing value={product.confidence} hue={hue} />
      </div>
      <span className={`ds-sl__fit is-${product.fit}`}>
        <i />
        {product.fit} fit
      </span>
      <p className="ds-sl__why">{product.why}</p>
      {/* The buyer-facing half. Rendered only when present, so a match cached
          before the prompt gained this field degrades to the rationale alone
          rather than showing an empty box. */}
      {product.benefit !== '' ? (
        <p className="ds-sl__benefit">
          <b>What you gain.</b> {product.benefit}
        </p>
      ) : null}
      {product.stages.length > 0 ? (
        <p className="ds-sl__owns mono">{product.stages.join(' → ')}</p>
      ) : null}
      {product.needs !== '' ? (
        <p className="ds-sl__needs">
          <b>Needs from you.</b> {product.needs}
        </p>
      ) : null}
    </article>
  );
}

/** A radial gauge for fit confidence — a ring rather than another progress bar. */
function FitRing({ value, hue }: { value: number; hue: string }): React.JSX.Element {
  const radius = 17;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.max(0, Math.min(1, value)));
  return (
    <span className="ds-sl__ring" role="img" aria-label={`fit confidence ${fmtPct(value)}`}>
      <svg width="42" height="42" viewBox="0 0 42 42" aria-hidden="true">
        <circle cx="21" cy="21" r={radius} fill="none" stroke="var(--bd2)" strokeWidth="3" />
        {/* `data-off` is the target the sweep animates to; the rendered
            `strokeDashoffset` is already the final value, so a ring is correct
            with no script. */}
        <circle
          className="is-val"
          cx="21"
          cy="21"
          r={radius}
          fill="none"
          stroke={hue}
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={circumference.toFixed(1)}
          strokeDashoffset={offset.toFixed(1)}
          data-off={offset.toFixed(1)}
        />
      </svg>
      <b className="mono">{Math.round(value * 100)}</b>
    </span>
  );
}

/**
 * The numbers behind the row, plus the two actions.
 *
 * The score lives here rather than on the card. It is real evidence and worth
 * keeping — it just cannot carry the ranking, and putting it in the headline
 * implied it could.
 */
function Evidence({
  row,
  onExplore,
  onAsk,
}: {
  row: ShortlistRowView;
  onExplore: (row: ShortlistRowView) => void;
  onAsk: () => void;
}): React.JSX.Element {
  const cells: [string, string][] = [
    ['Volume', `${fmtBig(row.signals.volume)} docs`],
    ['Pages', fmtBig(row.signals.pages)],
    ['Confidence', fmtConfidence(row.signals.avg_confidence)],
    ['Other rate', fmtPct(row.signals.other_rate)],
    ['Score', `${String(row.score)} / 100`],
    ['Window', row.window],
  ];
  return (
    <div className="ds-sl__ev is-in">
      <span className="ds-sl__evlab">Evidence</span>
      {cells.map(([label, value]) => (
        <div key={label} className="ds-sl__evcell">
          <b className="mono">{value}</b>
          <span>{label}</span>
        </div>
      ))}
      <div className="ds-sl__evacts">
        <button
          type="button"
          className="ds-linkbtn"
          onClick={() => {
            onExplore(row);
          }}
        >
          Explore these documents
        </button>
        <button type="button" className="ds-linkbtn" onClick={onAsk}>
          Ask
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------- states

/**
 * The honest footer: why the list is the length it is.
 *
 * `awaiting_match` in particular has to be visible. A type that qualifies on volume
 * but has not been matched yet is silently missing from the list otherwise, which
 * looks like the type not qualifying rather than the matcher not having run.
 */
function Footnotes({
  data,
}: {
  data: { awaiting_match: string[]; stale_match: string[]; qualified: number; window: number };
}): React.JSX.Element {
  const awaiting = data.awaiting_match;
  const stale = data.stale_match;
  return (
    <div className="ds-sl__notes">
      {awaiting.length > 0 ? (
        <p>
          <b>{awaiting.length} not matched yet</b> — {awaiting.slice(0, 4).join(', ')}
          {awaiting.length > 4 ? `, +${String(awaiting.length - 4)} more` : ''}. These clear the
          volume bar but have no product match on record; the nightly run will match them.
        </p>
      ) : null}
      {stale.length > 0 ? (
        <p>
          <b>{stale.length} matched against older text</b> — {stale.slice(0, 4).join(', ')}
          {stale.length > 4 ? `, +${String(stale.length - 4)} more` : ''}. Shown as-is; the next
          match run will refresh them.
        </p>
      ) : null}
      <p className="ds-sl__foot">
        {data.qualified} document types clear the volume bar; the busiest {data.window} are matched
        against the product catalogue. Volumes and confidences are recomputed from your current
        documents on every load.
      </p>
    </div>
  );
}

function BoltIcon(): React.JSX.Element {
  return (
    <svg width="26" height="26" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M8.8 1.5 L3.5 9 L7.4 9 L6.6 14.5 L12.5 7 L8.2 7 Z" fill="var(--a-ink)" />
    </svg>
  );
}

function NoCatalogue(): React.JSX.Element {
  return (
    <EmptyState
      icon={<BoltIcon />}
      title="No products in the catalogue yet"
      body="Automation recommendations match your document types against the product catalogue, and it hasn't been set up on this platform yet. A system administrator needs to seed it before this screen can recommend anything."
    />
  );
}

function NothingYet({
  data,
}: {
  data: { qualified: number; considered: number; min_volume: number; awaiting_match: string[] };
}): React.JSX.Element {
  const body =
    data.qualified === 0
      ? `Opportunities appear once a document type reaches ${fmtBig(data.min_volume)} documents in the trailing 12-month window. ${data.considered > 0 ? `You have ${String(data.considered)} classified types so far — as volume builds, the strongest cases surface here.` : 'Once documents start flowing in and getting classified, the strongest cases surface here.'}`
      : `${String(data.qualified)} document types clear the volume bar, but none has been matched against the product catalogue yet. The nightly run will match them.`;
  return <EmptyState icon={<BoltIcon />} title="Nothing to recommend yet" body={body} />;
}

function Loading(): React.JSX.Element {
  return (
    <section className="ds-sl" aria-label="Automation opportunities" aria-busy="true">
      <div className="ds-sl__top">
        <div style={{ flex: 1 }}>
          <Skeleton width="46%" height={19} radius={6} />
          <div style={{ marginTop: 10 }}>
            <Skeleton width="72%" height={12} radius={5} />
          </div>
        </div>
      </div>
      <div className="ds-sl__hero ds-sl__hero--skel">
        <Skeleton width="30%" height={26} radius={7} />
        <div style={{ marginTop: 22 }}>
          <Skeleton width="100%" height={44} radius={8} />
        </div>
        <div style={{ marginTop: 18, display: 'flex', gap: 14 }}>
          <Skeleton width="50%" height={150} radius={9} />
          <Skeleton width="50%" height={150} radius={9} />
        </div>
      </div>
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="ds-sl__row ds-sl__row--skel">
          <Skeleton width="100%" height={52} radius={9} />
        </div>
      ))}
    </section>
  );
}
